// Petite Sweet Shop · behaviour for the Petite sections (ported from the approved homepage mockup).
// Everything here enhances content that is already readable without script. Each part starts
// from its section element, so the theme editor can reload one section without the others.

const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- money, in the shop's own format ---------- */
const moneyIn = (format) => (cents) => {
  const n = Math.round(cents) / 100;
  const group = (v, d, t, s) => {
    const [int, dec] = v.toFixed(d).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (dec ? s + dec : '');
  };
  return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
    if (kind === 'amount_no_decimals') return group(n, 0, ',', '.');
    if (kind === 'amount_with_comma_separator') return group(n, 2, '.', ',');
    if (kind === 'amount_no_decimals_with_comma_separator') return group(n, 0, '.', ',');
    return group(n, 2, ',', '.');
  });
};
const fill = (text, values) => text.replace(/\[(\w+)\]/g, (m, k) => (k in values ? values[k] : m));

/* ---------- header: smoked glass as soon as the page moves, solid and compact from the second section ---------- */
function initHeader(header) {
  if (header.dataset.pReady) return;
  header.dataset.pReady = '1';

  // a page without a banner (a collection page whose banner was removed) keeps the solid header
  if (header.hasAttribute('data-overlay') && !document.querySelector('[data-p-hero]')) {
    header.removeAttribute('data-overlay');
    header.classList.add('p-site-header--static', 'p-is-compact');
  }

  if (header.hasAttribute('data-overlay')) {
    // On wide screens Horizon scrolls .page-wrapper, not the document; listen to both.
    let ticking = false;
    const scroller = () => (matchMedia('(min-width: 990px)').matches && document.querySelector('.page-wrapper')) || document.scrollingElement;
    const set = () => {
      ticking = false;
      const top = scroller().scrollTop;
      const hero = document.querySelector('[data-p-hero]');
      header.classList.toggle('p-is-solid', top > 4);
      header.classList.toggle('p-is-compact', hero ? hero.getBoundingClientRect().bottom <= header.offsetHeight : top > 4);
    };
    document.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(set); } }, { passive: true, capture: true });
    addEventListener('resize', set);
    set();
  }

  // the full-screen menu on small screens
  const toggle = header.querySelector('[data-p-menu-toggle]');
  const menu = document.querySelector('[data-p-menu]');
  if (toggle && menu) {
    const setMenu = (open) => {
      menu.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = open ? toggle.dataset.closeLabel : toggle.dataset.openLabel;
      document.body.style.overflow = open ? 'hidden' : '';
    };
    toggle.addEventListener('click', () => setMenu(menu.hidden));
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) setMenu(false); });
    addEventListener('keydown', (e) => { if (e.key === 'Escape' && !menu.hidden) setMenu(false); });
  }

  // the cart pill opens Horizon's cart drawer when there is one, and keeps its count current
  const cart = header.querySelector('[data-p-cart]');
  cart?.addEventListener('click', (e) => {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') { e.preventDefault(); drawer.open(); }
  });
}

// The cart is counted in boxes: a box line (it carries _cap) counts once per box, its treats not at
// all, and anything outside a box by its quantity.
const boxCount = (items = []) => {
  const boxes = new Set(items.filter((i) => i.properties?._cap).map((i) => i.properties._box));
  return items.reduce((n, i) => {
    const p = i.properties || {};
    if (p._cap) return n + i.quantity;
    return p._box && boxes.has(p._box) ? n : n + i.quantity;
  }, 0);
};
const setCartCount = (count) => {
  document.querySelectorAll('[data-p-cart]').forEach((cart) => {
    const el = cart.querySelector('[data-p-cart-count]');
    if (!el || el.textContent === String(count)) return;
    el.textContent = count;
    cart.classList.remove('p-bump'); void cart.offsetWidth; cart.classList.add('p-bump');
  });
};
import('@shopify/events')
  .then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then(({ cart, detail }) => {
        if (detail?.items) return setCartCount(boxCount(detail.items));
        fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json()).then((c) => setCartCount(boxCount(c.items))).catch(() => {});
      }).catch(() => {});
    });
  })
  .catch(() => {});

/* ---------- hero: the box with its lid lifted, then the same box fully open ---------- */
// Once the opening zoom has settled, the photo fades to the open box and back, again and again.
// The open box eases in by as much as the opening zoom came out, and the closed box eases back out.
// It rests while the hero is out of view; with reduced motion only the first photo shows.
function initHero(hero) {
  const open = hero.querySelector('.p-hero-open');
  if (!open || reduce || hero.dataset.pReady) return;
  hero.dataset.pReady = '1';
  const SETTLE = 3200, HOLD = +hero.dataset.hold || 4500;
  const media = open.parentElement;
  let seen = true;
  const swap = () => {
    if (!open.isConnected) return;
    if (seen) media.classList.toggle('p-is-near', open.classList.toggle('p-is-on'));
    setTimeout(swap, HOLD);
  };
  const start = () => {
    open.srcset = open.dataset.srcset;
    open.decode().then(() => setTimeout(swap, Math.max(0, SETTLE - performance.now())), () => open.remove());
  };
  document.readyState === 'complete' ? start() : addEventListener('load', start, { once: true });
  new IntersectionObserver(([e]) => { seen = e.isIntersecting; }).observe(media);
}

/* ---------- build a box ---------- */
// Note-card faces beyond the brand's own load only when someone asks for them.
const loadedFonts = new Set(['Pinyon Script', 'Fraunces Italic']);
const loadFont = (family) => {
  if (loadedFonts.has(family)) return Promise.resolve();
  loadedFonts.add(family);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}&display=swap`;
  document.head.append(link);
  return new Promise((r) => link.addEventListener('load', () => document.fonts.load(`20px '${family}'`).then(r, r)));
};

// Fit lid lettering inside the printable area of the lid (viewBox units).
const fitWords = (text, max = 540, base) => {
  const size = base || parseFloat(getComputedStyle(text).fontSize) || 92;
  text.style.fontSize = size + 'px';
  const width = text.getComputedTextLength();
  if (width > max) text.style.fontSize = (size * max) / width + 'px';
};

function initBuilder(builder) {
  if (builder.dataset.pReady) return;
  builder.dataset.pReady = '1';
  const d = builder.dataset;
  const money = moneyIn(d.moneyFormat);
  const treats = [...builder.querySelectorAll('[data-treat]')];
  const sizes = [...builder.querySelectorAll('input[name=size]')];
  const rowsEl = builder.querySelector('[data-slip-rows]');
  const totalEl = builder.querySelector('[data-total]');
  const noteEl = builder.querySelector('[data-slip-note]');
  const fillEl = builder.querySelector('[data-fill]');
  const preview = builder.querySelector('[data-preview-words]');
  const presetBox = builder.querySelector('[data-lid-preset]');
  const customBox = builder.querySelector('[data-lid-custom]');
  const sayingSelect = builder.querySelector('[data-saying-select]');
  const customInput = builder.querySelector('[data-custom-words]');
  const note = builder.querySelector('[data-note]');
  const cardText = builder.querySelector('[data-card-text]');
  const addBox = builder.querySelector('[data-add-box]');
  const addLabel = addBox.textContent;
  let lastRows = [];

  // Phones: the bar shows while the builder is on screen and steps aside when the receipt itself is.
  const bar = builder.querySelector('[data-buildbar]');
  if (bar) {
    const slip = builder.querySelector('.p-slip-wrap');
    let inBuild = false, slipSeen = false;
    const show = () => bar.classList.toggle('p-is-on', inBuild && !slipSeen);
    new IntersectionObserver(([e]) => { inBuild = e.isIntersecting; show(); }, { rootMargin: '-30% 0px -30% 0px' }).observe(builder);
    new IntersectionObserver(([e]) => { slipSeen = e.isIntersecting; show(); }, { threshold: 0.35 }).observe(slip);
    bar.querySelector('[data-bar-jump]').addEventListener('click', () => slip.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }));
    bar.querySelector('[data-bar-add]').addEventListener('click', () => addBox.click());
  }
  if (!sizes.some((r) => r.checked) && sizes.length) sizes[Math.min(1, sizes.length - 1)].checked = true;

  const state = () => {
    const size = sizes.find((r) => r.checked);
    const lidMode = builder.querySelector('input[name=lid]:checked').value;
    return {
      size, label: size?.dataset.label || '', cap: +(size?.dataset.cap || 0), price: +(size?.dataset.price || 0),
      // own words: the gap to this box's second lid variant
      engraving: size ? +size.dataset.priceOwn - +size.dataset.price : +d.engraving || 0,
      filled: treats.reduce((n, t) => n + +t.dataset.qty, 0),
      lidMode, words: lidMode === 'custom' ? customInput.value.trim() || customInput.placeholder : sayingSelect.value,
      font: builder.querySelector('input[name=font]:checked')?.value || '',
    };
  };

  const render = () => {
    const s = state();
    // the box price carries the lid, the card and the sticker; treats follow at their own price
    const rows = [[fill(d.rowBox, { size: s.label, cap: s.cap }), money(s.price)]];
    let total = s.price;
    if (s.lidMode === 'custom') { rows.push([`${d.rowWords}: “${s.words}”`, '+' + money(s.engraving), 'sub']); total += s.engraving; }
    else rows.push([`${d.rowLid}: “${s.words}”`, d.rowIncluded, 'sub']);
    rows.push([d.rowExtras, d.rowIncluded, 'sub']);
    treats.forEach((t) => {
      const q = +t.dataset.qty;
      t.querySelector('output').textContent = q;
      t.querySelector("[data-step='-1']").disabled = q === 0;
      t.querySelector("[data-step='1']").disabled = s.filled >= s.cap;
      if (q) { rows.push([`${q} × ${t.dataset.name}`, money(q * +t.dataset.price)]); total += q * +t.dataset.price; }
    });

    rowsEl.replaceChildren(...rows.map(([a, b, sub]) => {
      const li = document.createElement('li');
      li.innerHTML = '<span></span><span></span>';
      li.children[0].textContent = a; li.children[1].textContent = b;
      if (sub) li.classList.add('p-slip-sub');
      if (lastRows.length && !lastRows.includes(a)) li.classList.add('p-row-in');
      return li;
    }));
    lastRows = rows.map((r) => r[0]);
    totalEl.textContent = money(total);

    fillEl.textContent = fill(fillEl.dataset.of, { filled: s.filled, cap: s.cap });
    fillEl.classList.toggle('p-is-full', s.filled === s.cap);
    const left = s.cap - s.filled;
    noteEl.textContent = s.filled === 0 ? d.msgEmpty : left === 0 ? d.msgFull : fill(d.msgRoom, { left });
    addBox.disabled = s.filled === 0;
    if (bar) {
      bar.querySelector('[data-bar-line]').textContent = fill(bar.dataset.line, { size: s.label, filled: s.filled, cap: s.cap });
      bar.querySelector('[data-bar-meter]').style.width = `${s.cap ? (s.filled / s.cap) * 100 : 0}%`;
      bar.querySelector('[data-bar-total]').textContent = totalEl.textContent;
      bar.querySelector('[data-bar-add]').disabled = addBox.disabled;
    }

    preview.textContent = s.words;
    fitWords(preview, 540, 96);
    cardText.textContent = note.value.trim() || d.cardEmpty;
  };

  // A new size always starts full. A smaller box gives back treats from the end of the list; a
  // bigger one first takes back what a smaller box gave back, then adds the treats it holds fewest of.
  const given = {};
  const qty = (t) => +t.dataset.qty;
  const trim = () => {
    let over = state().filled - state().cap;
    for (const t of [...treats].reverse()) {
      if (over <= 0) break;
      const take = Math.min(qty(t), over);
      t.dataset.qty = qty(t) - take; over -= take;
      given[t.dataset.treat] = (given[t.dataset.treat] || 0) + take;
    }
  };
  const topUp = () => {
    let room = state().cap - state().filled;
    for (const t of treats) {
      const back = Math.min(given[t.dataset.treat] || 0, room);
      t.dataset.qty = qty(t) + back; given[t.dataset.treat] -= back; room -= back;
    }
    for (; room > 0 && treats.length; room--) {
      const t = treats.reduce((a, b) => (qty(b) < qty(a) ? b : a));
      t.dataset.qty = qty(t) + 1;
    }
  };
  const fitSize = () => { trim(); topUp(); };

  builder.addEventListener('click', (e) => {
    const b = e.target.closest('[data-step]');
    if (!b) return;
    const t = b.closest('[data-treat]');
    t.dataset.qty = Math.max(0, +t.dataset.qty + +b.dataset.step);
    render();
  });
  builder.addEventListener('change', (e) => {
    if (e.target.name === 'size') fitSize();
    if (e.target.name === 'lid') {
      const custom = e.target.value === 'custom';
      presetBox.hidden = custom; customBox.hidden = !custom;
      if (custom) customInput.focus();
    }
    if (e.target.name === 'font') {
      const family = e.target.value;
      loadFont(family).then(() => {
        const serif = family === 'Fraunces Italic';
        for (const el of [note, cardText]) {
          el.style.fontFamily = serif ? 'Fraunces, serif' : `'${family}', cursive`;
          el.style.fontStyle = serif ? 'italic' : 'normal';
        }
        note.style.fontSize = serif ? '22px' : '';
        cardText.style.fontSize = serif ? '17px' : '';
      });
    }
    render();
  });
  builder.querySelector('form').addEventListener('submit', (e) => e.preventDefault());
  customInput.addEventListener('input', render);
  note.addEventListener('input', render);
  // each lettering chip is set in its own face; the extra faces load as the builder comes near
  const samples = [...builder.querySelectorAll('[data-font-sample]')];
  new IntersectionObserver(([e], obs) => {
    if (!e.isIntersecting) return;
    obs.disconnect();
    samples.forEach((el) => loadFont(el.dataset.fontSample).then(() => (el.style.fontFamily = `'${el.dataset.fontSample}', cursive`)));
  }, { rootMargin: '600px 0px' }).observe(builder);

  // "Add to your box": one more of a treat. A full box moves up a size, filled, with the treat in it.
  builder.addTreat = (key) => {
    const t = treats.find((x) => x.dataset.treat === key);
    if (!t) return 'missing';
    const s = state();
    if (s.filled < s.cap) t.dataset.qty = qty(t) + 1;
    else {
      const bigger = sizes.find((r) => +r.dataset.cap > s.cap);
      if (!bigger) return 'full';
      sizes.forEach((r) => (r.checked = r === bigger));
      t.dataset.qty = qty(t) + 1;
      topUp();
    }
    render();
    return 'added';
  };

  // Links from the shop page start the builder from a size (?size=small-gift-box, or the size name),
  // full, or add a treat to the box (?add=almond-snowball) the way the treats menu does.
  const params = new URLSearchParams(location.search);
  // on a box's own page the builder starts from that box
  const wantSize = (params.get('size') || builder.dataset.startSize || '').toLowerCase();
  const wantTreat = params.get('add');
  const pick = sizes.find((r) => wantSize && (r.dataset.handle === wantSize || r.dataset.label.toLowerCase() === wantSize));
  if (pick) { sizes.forEach((r) => (r.checked = r === pick)); fitSize(); }
  if (wantTreat) builder.addTreat(wantTreat);

  // Step 2 (#fill), with the row of the treat just added lit up once. The shop page's links land
  // there too; the jump is made again once the photographs above have loaded and moved it.
  const fillStep = builder.querySelector('[data-fill-step]');
  builder.showTreat = (key, smooth = !reduce) => {
    fillStep?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    const row = treats.find((t) => t.dataset.treat === key);
    if (!row) return;
    setTimeout(() => row.classList.add('p-is-new'), 350);
    setTimeout(() => row.classList.remove('p-is-new'), 1800);
  };
  if (wantTreat && fillStep && location.hash === '#' + fillStep.id) {
    const land = () => builder.showTreat(wantTreat, false);
    if (document.readyState === 'complete') land(); else addEventListener('load', land, { once: true });
  }


  // Add the box to the cart, the way Horizon's own product form does, so its cart drawer and count
  // follow: announce the lines with a promise, post them, then resolve it with the new cart.
  // The box line (the lid variant: a saying, or own words) carries the lid words, the note and the
  // lettering; each treat follows as its own line. Every line gets the same visible "Box 2" label
  // and a hidden _box id, so the order shows which treats go in which box.
  const fail = () => { noteEl.textContent = d.msgError; addBox.disabled = false; addBox.textContent = addLabel; };
  const readCart = async () => {
    const items = document.querySelector('cart-items-component');
    if (items?.fetchCartData) { await customElements.whenDefined('cart-items-component'); return items.fetchCartData(); }
    return (await fetch(d.cartUrl + '.js', { headers: { Accept: 'application/json' } })).json();
  };
  addBox.addEventListener('click', async () => {
    const s = state();
    const own = s.lidMode === 'custom';
    const boxVariant = +(own ? s.size?.dataset.variantOwn : s.size?.dataset.variant);
    const treatLines = treats.filter((t) => +t.dataset.qty).map((t) => ({ id: +t.dataset.variant, quantity: +t.dataset.qty }));
    if (!boxVariant || treatLines.some((l) => !l.id)) return fail();

    addBox.disabled = true;
    let events;
    try { events = await import('@shopify/events'); } catch { events = null; }
    try {
      const before = await readCart();
      const boxes = new Set(before.items.map((i) => i.properties?._box).filter(Boolean));
      const label = `${d.propBox} ${boxes.size + 1}`;
      const id = Date.now().toString(36);
      const boxProps = { [d.propBox]: label, [d.propLid]: s.words };
      if (note.value.trim()) boxProps[d.propNote] = note.value.trim();
      if (s.font) boxProps[d.propFont] = s.font;
      boxProps._box = id;
      // hidden copies for the cart's box card, whatever the visible labels are called
      Object.assign(boxProps, { _cap: String(s.cap), _lid: s.words, _own: own ? '1' : '0' });
      if (note.value.trim()) boxProps._note = note.value.trim();
      if (s.font) boxProps._font = s.font;
      // the cart lists the last line first, so the box goes last and shows above its treats
      const items = [...treatLines.map((l) => ({ ...l, properties: { [d.propBox]: label, _box: id } })),
        { id: boxVariant, quantity: 1, properties: boxProps }];

      const sections = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
      const deferred = events?.CartLinesUpdateEvent.createPromise();
      if (deferred) {
        builder.dispatchEvent(new events.CartLinesUpdateEvent({
          action: 'add', context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: deferred.promise,
        }));
      }
      const res = await (await fetch(d.cartAdd, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      })).json();
      const cart = await readCart();
      const ok = !res.status;
      deferred?.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'petite-builder', sourceId: builder.id, itemCount: items.reduce((n, i) => n + i.quantity, 0), sections: ok ? res.sections : undefined, didError: !ok },
      });
      setCartCount(cart.item_count);
      if (!ok) return fail();

      addBox.textContent = d.msgAdded;
      const drawer = document.getElementById('cart-drawer');
      if (drawer?.open && !drawer.isOpen) drawer.open(); else if (!drawer) location.href = d.cartUrl;
      setTimeout(() => { addBox.textContent = addLabel; addBox.disabled = false; }, 2400);
    } catch { fail(); }
  });

  document.fonts.ready.then(render);
  render();
}

/* ---------- the treats menu talks to the builder on the same page ---------- */
function initTreats(section) {
  if (section.dataset.pReady) return;
  section.dataset.pReady = '1';
  section.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-add-treat]');
    if (!btn) return;
    const builder = document.querySelector('[data-p-builder]');
    const result = builder?.addTreat?.(btn.dataset.addTreat);
    if (result === 'full') { btn.textContent = btn.dataset.full; builder.showTreat(); return; }
    if (result !== 'added') { if (builder) builder.scrollIntoView(); return; }
    // the box is filled in step 2 of the builder, so the visitor is taken there to see it
    btn.textContent = btn.dataset.added;
    builder.showTreat(btn.dataset.addTreat);
    setTimeout(() => (btn.textContent = btn.dataset.label), 2200);
  });
}

/* ---------- touch screens: a tap shows a treat's second photograph, as pointing does ---------- */
let touched = false;
document.addEventListener('pointerdown', (e) => { touched = e.pointerType !== 'mouse'; }, true);
document.addEventListener('click', (e) => {
  if (!touched) return;
  const card = e.target.closest('.p-treat, .p-item, .p-treat-card');
  if (!card?.querySelector('.p-swap-real, .p-treat-real')) return;
  // buttons and links keep their own job; the shop's photo link opens the product on a second tap
  const control = e.target.closest('a, button, input, label');
  if (control && !control.matches('.p-treat-photo')) return;
  if (control) { if (card.classList.contains('p-is-swapped')) return; e.preventDefault(); }
  document.querySelectorAll('.p-is-swapped').forEach((c) => c !== card && c.classList.remove('p-is-swapped'));
  card.classList.toggle('p-is-swapped');
});

/* ---------- the cart: a box and its treats change together ---------- */
// Each box card carries its lines (the box, and each treat with how many go in one box). The
// Boxes stepper sets every line to that many boxes' worth; Remove sets them all to none. The
// drawer and the cart page are then drawn again from the answer, as Horizon does for its own lines.
let cartBusy = false;
async function changeBox(card, boxes) {
  if (cartBusy) return;
  cartBusy = true;
  const lines = JSON.parse(card.dataset.lines || '[]');
  const updates = Object.fromEntries(lines.map((l) => [l.key, l.per * boxes]));
  const holders = [...document.querySelectorAll('cart-items-component')];
  const sections = holders.map((el) => el.dataset.sectionId).filter(Boolean);
  holders.forEach((el) => el.classList.add('p-is-busy'));
  try {
    const res = await fetch(`${window.Theme?.routes?.cart_update_url || '/cart/update'}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sections.join(','), sections_url: location.pathname }),
    });
    const cart = await res.json();
    if (!res.ok || cart.status) throw new Error(cart.description || 'cart');
    const { morphSection } = await import('@theme/section-renderer');
    for (const el of holders) {
      const html = cart.sections?.[el.dataset.sectionId];
      if (html) await morphSection(el.dataset.sectionId, html, { mode: el.hasAttribute('data-drawer') ? 'hydration' : 'full' });
    }
    setCartCount(boxCount(cart.items));
  } catch {
    card.querySelector('.p-cbox-actions')?.setAttribute('data-error', '');
  } finally {
    holders.forEach((el) => el.classList.remove('p-is-busy'));
    cartBusy = false;
  }
}
document.addEventListener('click', (e) => {
  const card = e.target.closest('[data-p-box]');
  if (!card) return;
  const step = e.target.closest('[data-p-box-step]');
  if (step) {
    const next = Math.max(1, +card.dataset.qty + +step.dataset.pBoxStep);
    if (next === +card.dataset.qty) return;
    card.querySelector('.p-stepper output').textContent = next;
    changeBox(card, next);
    return;
  }
  if (e.target.closest('[data-p-box-remove]')) {
    card.classList.add('p-is-leaving');
    const go = () => changeBox(card, 0);
    reduce ? go() : setTimeout(go, 260);
  }
});

// The drawer lies over the page rather than pushing it aside; a click on the page beside it closes it.
document.addEventListener('click', (e) => {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer?.hasAttribute('open')) return;
  const panel = drawer.querySelector('dialog');
  // a link inside it to a place on this same page (Build a box on the home page) closes it too
  const link = e.target.closest('a[href*="#"]');
  if (link && panel?.contains(link) && new URL(link.href).pathname === location.pathname) {
    // close first, then go: the drawer hands focus back as it closes, which would stop the scroll
    const target = document.getElementById(new URL(link.href).hash.slice(1));
    if (!target) return drawer.close?.();
    e.preventDefault();
    drawer.close?.();
    setTimeout(() => target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }), 360);
    return;
  }
  if (panel?.contains(e.target) || e.target.closest('[data-p-cart], [data-add-box], [data-bar-add]')) return;
  drawer.close?.();
});

/* ---------- section entrances: each part in reading order, once ---------- */
const io = new IntersectionObserver((entries) => {
  for (const e of entries) if (e.isIntersecting) { e.target.classList.add('p-is-in'); io.unobserve(e.target); }
}, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
function initReveal(root) {
  root.querySelectorAll('.p-scope').forEach((sec) => {
    sec.querySelectorAll('[data-reveal]').forEach((el, i) => el.style.setProperty('--p-d', `${Math.min(i, 6) * 80}ms`));
  });
  root.querySelectorAll('[data-reveal]:not(.p-is-in)').forEach((el) => {
    // the theme editor shows everything at once, so nothing waits off screen there
    if (window.Shopify?.designMode) el.classList.add('p-is-in'); else io.observe(el);
  });
}

/* ---------- a treat's page: its two photographs trade places on a click or tap ---------- */
function initGallery(gallery) {
  if (gallery.dataset.pReady) return;
  gallery.dataset.pReady = '1';
  const big = gallery.querySelector('.p-pt-main img');
  const small = gallery.querySelector('.p-pt-thumb img');
  if (!big || !small) return;
  gallery.querySelector('.p-pt-thumb').addEventListener('click', () => {
    // each photograph keeps its own fit: the cut-out stands free, the linen fills the frame
    for (const key of ['src', 'srcset', 'alt', 'className']) [big[key], small[key]] = [small[key], big[key]];
    small.loading = 'eager';
  });
}

/* ---------- the contact note: it asks for what the chosen topic needs ---------- */
function initContact(section) {
  if (section.dataset.pReady) return;
  section.dataset.pReady = '1';
  const extras = [...section.querySelectorAll('[data-for]')];
  const show = () => {
    const topic = section.querySelector('[data-topic]:checked')?.dataset.topic;
    extras.forEach((el) => {
      const on = el.dataset.for.split(' ').includes(topic);
      el.hidden = !on;
      // a hidden field stays out of the email
      el.querySelectorAll('input, select').forEach((f) => (f.disabled = !on));
    });
  };
  section.addEventListener('change', (e) => { if (e.target.matches('[data-topic]')) show(); });
  show();
  // after sending, the thank-you takes the focus so it is read out
  section.querySelector('[data-p-done]')?.focus();
}

/* ---------- the policies page: the side list marks the part being read ---------- */
function initPolicies(section) {
  if (section.dataset.pReady) return;
  section.dataset.pReady = '1';
  const links = new Map([...section.querySelectorAll('.p-policies-nav a')].map((a) => [a.hash.slice(1), a]));
  const mark = (id) => links.forEach((a, key) => (key === id ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current')));
  const seen = new Set();
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => (e.isIntersecting ? seen.add(e.target.id) : seen.delete(e.target.id)));
    // the first part in the reading band, in page order
    const first = [...links.keys()].find((id) => seen.has(id));
    if (first) mark(first);
  }, { rootMargin: '-25% 0px -65% 0px' });
  section.querySelectorAll('.p-policies-part[id]').forEach((part) => io.observe(part));
  links.forEach((a, id) => a.addEventListener('click', () => mark(id)));
}

function init(root = document) {
  root.querySelectorAll('[data-p-header]').forEach(initHeader);
  root.querySelectorAll('[data-p-hero]').forEach(initHero);
  root.querySelectorAll('[data-p-builder]').forEach(initBuilder);
  root.querySelectorAll('[data-p-treats]').forEach(initTreats);
  root.querySelectorAll('[data-p-gallery]').forEach(initGallery);
  root.querySelectorAll('[data-p-contact]').forEach(initContact);
  root.querySelectorAll('[data-p-policies]').forEach(initPolicies);
  initReveal(root);
}

init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
